import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, QrCode as QrIcon, Trash2, Download, Eye, User, Baby, Heart, Car, Dog, Briefcase, Package, HeartHandshake } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api, BACKEND_URL } from "@/lib/api";
import PageHero from "@/components/PageHero";
import StoryPromptDialog from "@/components/StoryPromptDialog";

const TYPE_META = {
  person: { icon: User, label: "Person" },
  child: { icon: Baby, label: "Child" },
  senior: { icon: Heart, label: "Senior" },
  vehicle: { icon: Car, label: "Vehicle" },
  pet: { icon: Dog, label: "Pet" },
  luggage: { icon: Briefcase, label: "Luggage" },
  lostfound: { icon: Package, label: "Lost & Found" },
};

const EMPTY = {
  type: "person",
  label: "",
  display_name: "",
  blood_group: "",
  allergies: "",
  medical_notes: "",
  address: "",
  emergency_contacts: [{ name: "", phone: "", relation: "" }],
  vehicle_number: "",
  vehicle_model: "",
  pet_breed: "",
  item_description: "",
  reward: "",
  show_owner_phone: true,
  is_public: true,
};

export default function QRManage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [viewQR, setViewQR] = useState(null);
  const [storyOpen, setStoryOpen] = useState(false);
  const [dismissedReunions, setDismissedReunions] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("g360_reunion_prompted") || "[]")); } catch { return new Set(); }
  });

  const load = async () => {
    const { data } = await api.get("/qr");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.label || !form.display_name) {
      toast.error("Label and display name required");
      return;
    }
    try {
      const payload = { ...form, emergency_contacts: form.emergency_contacts.filter((c) => c.name && c.phone) };
      await api.post("/qr", payload);
      toast.success("QR profile created");
      setForm(EMPTY);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this QR profile?")) return;
    await api.delete(`/qr/${id}`);
    toast.success("Deleted");
    load();
  };

  const markReunited = async (id) => {
    try {
      await api.post(`/qr/${id}/reunited`);
      toast.success("Wonderful — glad it made it home!");
      await load();
      // Prompt for a testimonial once per item
      if (!dismissedReunions.has(id)) {
        const next = new Set(dismissedReunions);
        next.add(id);
        setDismissedReunions(next);
        localStorage.setItem("g360_reunion_prompted", JSON.stringify([...next]));
        setStoryOpen(true);
      }
    } catch (e) {
      toast.error("Failed to mark reunited");
    }
  };

  return (
    <div className="max-w-6xl">
      <PageHero
        kicker="Digital identity"
        title="My QR profiles"
        subtitle="Create scannable safety profiles for people, vehicles, pets and items."
        image="https://images.unsplash.com/photo-1587440871875-191322ee64b0?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80"
        testId="qr-hero"
      />
      <div className="flex justify-end mb-6">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-qr-btn" className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white rounded-full">
              <Plus className="w-4 h-4 mr-2" /> New QR profile
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create QR profile</DialogTitle>
              <DialogDescription>Fill in the details that should appear when someone scans this QR code.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="qr-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TYPE_META).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Label (private)</Label>
                  <Input data-testid="qr-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Kid's backpack" />
                </div>
              </div>
              <div>
                <Label>Display name (public)</Label>
                <Input data-testid="qr-display-name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="Aarav Sharma" />
              </div>
              {(form.type === "person" || form.type === "child" || form.type === "senior") && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Blood group</Label>
                    <Input value={form.blood_group} onChange={(e) => setForm({ ...form, blood_group: e.target.value })} placeholder="O+" />
                  </div>
                  <div>
                    <Label>Allergies</Label>
                    <Input value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} placeholder="Peanuts, Penicillin" />
                  </div>
                </div>
              )}
              {form.type === "vehicle" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Vehicle number</Label>
                    <Input value={form.vehicle_number} onChange={(e) => setForm({ ...form, vehicle_number: e.target.value })} placeholder="KA-01-AB-4231" />
                  </div>
                  <div>
                    <Label>Model</Label>
                    <Input value={form.vehicle_model} onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })} placeholder="Tesla Model 3" />
                  </div>
                </div>
              )}
              {form.type === "pet" && (
                <div>
                  <Label>Breed</Label>
                  <Input value={form.pet_breed} onChange={(e) => setForm({ ...form, pet_breed: e.target.value })} placeholder="Golden Retriever" />
                </div>
              )}
              {(form.type === "lostfound" || form.type === "luggage") && (
                <>
                  <div>
                    <Label>Item description</Label>
                    <Textarea value={form.item_description} onChange={(e) => setForm({ ...form, item_description: e.target.value })} placeholder="Black leather wallet with 3 cards" />
                  </div>
                  <div>
                    <Label>Reward (optional)</Label>
                    <Input value={form.reward} onChange={(e) => setForm({ ...form, reward: e.target.value })} placeholder="$50" />
                  </div>
                </>
              )}
              <div>
                <Label>Medical notes / instructions</Label>
                <Textarea value={form.medical_notes} onChange={(e) => setForm({ ...form, medical_notes: e.target.value })} placeholder="Diabetic, insulin in bag" />
              </div>
              <div>
                <Label>Address (optional, for return)</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>

              <div>
                <Label>Emergency contacts</Label>
                {form.emergency_contacts.map((c, idx) => (
                  <div key={idx} className="grid grid-cols-3 gap-2 mt-2">
                    <Input placeholder="Name" value={c.name} onChange={(e) => {
                      const arr = [...form.emergency_contacts]; arr[idx].name = e.target.value; setForm({ ...form, emergency_contacts: arr });
                    }} />
                    <Input placeholder="Phone" value={c.phone} onChange={(e) => {
                      const arr = [...form.emergency_contacts]; arr[idx].phone = e.target.value; setForm({ ...form, emergency_contacts: arr });
                    }} />
                    <Input placeholder="Relation" value={c.relation} onChange={(e) => {
                      const arr = [...form.emergency_contacts]; arr[idx].relation = e.target.value; setForm({ ...form, emergency_contacts: arr });
                    }} />
                  </div>
                ))}
                <Button variant="ghost" size="sm" className="mt-2" onClick={() => setForm({ ...form, emergency_contacts: [...form.emergency_contacts, { name: "", phone: "", relation: "" }] })}>
                  + Add contact
                </Button>
              </div>

              <div className="flex items-center justify-between pt-2">
                <div>
                  <div className="text-sm font-medium">Show my phone to scanner</div>
                  <div className="text-xs text-muted-foreground">Owners phone is shown on public page</div>
                </div>
                <Switch checked={form.show_owner_phone} onCheckedChange={(v) => setForm({ ...form, show_owner_phone: v })} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Public profile</div>
                  <div className="text-xs text-muted-foreground">Disable to make scans private</div>
                </div>
                <Switch checked={form.is_public} onCheckedChange={(v) => setForm({ ...form, is_public: v })} />
              </div>

              <Button data-testid="save-qr-btn" onClick={save} className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">
                Create QR profile
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-16 text-center">
          <QrIcon className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <div className="font-medium mb-1">No QR profiles yet</div>
          <div className="text-sm text-muted-foreground">Create one for yourself, your child, or your car.</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it, i) => {
            const Meta = TYPE_META[it.type] || TYPE_META.person;
            const Icon = Meta.icon;
            return (
              <motion.div
                key={it.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className="rounded-2xl border border-border/70 bg-card p-5 hover:border-[hsl(var(--primary))]/50 transition-colors"
                data-testid={`qr-card-${it.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary))]/10 grid place-items-center">
                    <Icon className="w-5 h-5 text-[hsl(var(--primary))]" />
                  </div>
                  <Badge variant="outline" className="text-xs uppercase tracking-wider">{Meta.label}</Badge>
                </div>
                <div className="font-display font-bold text-lg tracking-tight">{it.display_name}</div>
                <div className="text-xs text-muted-foreground mb-4">{it.label}</div>
                <div className="text-xs text-muted-foreground border-t border-border/50 pt-3 mb-4 flex items-center justify-between">
                  <span>{it.scan_count} scans</span>
                  <span>{new Date(it.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setViewQR(it)} data-testid={`view-qr-${it.id}`}><Eye className="w-3.5 h-3.5" /></Button>
                  <Button asChild variant="outline" size="sm">
                    <Link to={`/scan/${it.id}`} target="_blank" rel="noreferrer"><QrIcon className="w-3.5 h-3.5" /></Link>
                  </Button>
                  {(it.type === "lostfound" || it.type === "luggage") && !it.reunited_at && it.scan_count > 0 && (
                    <Button
                      size="sm"
                      onClick={() => markReunited(it.id)}
                      className="bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--secondary))]/90 text-white"
                      data-testid={`reunited-${it.id}`}
                    >
                      <HeartHandshake className="w-3.5 h-3.5 mr-1" /> Reunited
                    </Button>
                  )}
                  {it.reunited_at && (
                    <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--secondary))] self-center px-2">Returned</span>
                  )}
                  <Button variant="outline" size="sm" onClick={() => remove(it.id)} className="ml-auto text-[hsl(var(--destructive))]" data-testid={`delete-qr-${it.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      <Dialog open={!!viewQR} onOpenChange={() => setViewQR(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{viewQR?.display_name}</DialogTitle>
            <DialogDescription>Print or download this QR code and attach it to your item.</DialogDescription>
          </DialogHeader>
          {viewQR && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="p-6 rounded-2xl bg-white">
                <QRCodeCanvas
                  id={`qr-${viewQR.id}`}
                  value={`${window.location.origin}/scan/${viewQR.id}`}
                  size={240}
                  level="H"
                  includeMargin
                />
              </div>
              <div className="text-xs text-muted-foreground text-center break-all">{window.location.origin}/scan/{viewQR.id}</div>
              <Button
                data-testid="download-qr-btn"
                onClick={() => {
                  const c = document.getElementById(`qr-${viewQR.id}`);
                  const url = c.toDataURL("image/png");
                  const a = document.createElement("a"); a.href = url; a.download = `guardian360-${viewQR.label}.png`; a.click();
                }}
                className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white"
              >
                <Download className="w-4 h-4 mr-2" /> Download PNG
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <StoryPromptDialog
        open={storyOpen}
        onOpenChange={setStoryOpen}
        title="Great news — glad it made it home!"
        description="Would you take a moment to share how Guardian360 helped? Approved stories may appear on our homepage."
      />
    </div>
  );
}
